import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useHistory} from 'src/rerouter/index';
import {useSelector, useDispatch} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  SubTitleLarge,
  SubTitleSmall,
  BodyLargeBold,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';

import {
  Panel,
  PanelBody,
  PanelHeader,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Checkbox} from '@spaced-out/ui-design-system/lib/components/Checkbox';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';

import {candidateResumeParsing} from 'src/action-creators/crm-new/index';

import {selectLocations, selectParsingCandidate} from 'src/selectors/crm';

import {InfoCard} from './info-card';

import css from './parsing.css';


export const CrmParsing = ({showParsingPanel}: {showParsingPanel: boolean}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const [newResumesPage, setNewResumesPage] = React.useState(0);
  const [processedResumesPage, setProcessedResumesPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [showUnparsed, setShowUnparsed] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState({
    new: false,
    processed: false,
  });
  // Add initial loading state
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);

  // Move intervalId to ref to persist across renders
  const intervalIdRef = React.useRef(null);

  const parsingCandidate = useSelector(selectParsingCandidate);

  // Keep track of all loaded resumes
  const [allNewResumes, setAllNewResumes] = React.useState([]);
  const [allProcessedResumes, setAllProcessedResumes] = React.useState([]);

  React.useEffect(() => {
    if (parsingCandidate?.candidates) {
      const newOnes = parsingCandidate.candidates.filter((c) => !c.tried);
      const processedOnes = parsingCandidate.candidates.filter((c) => c.tried);

      // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => CANDIDATE_PARSING[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setAllNewResumes((prev) => {
        const combined = [...newOnes];
        // Remove duplicates based on addedOn and sort by addedOn descending
        return Array.from(
          new Map(combined.map((item) => [item.addedOn, item])).values(),
          // @ts-expect-error - TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type. | TS2363 - The right-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
        ).sort((a, b) => new Date(b.addedOn) - new Date(a.addedOn));
      });

      // @ts-expect-error - TS2345 - Argument of type '(prev: never[]) => CANDIDATE_PARSING[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
      setAllProcessedResumes((prev) => {
        const combined = [...prev, ...processedOnes];
        // Remove duplicates based on addedOn and sort by addedOn descending
        return Array.from(
          new Map(combined.map((item) => [item.addedOn, item])).values(),
          // @ts-expect-error - TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type. | TS2363 - The right-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
        ).sort((a, b) => new Date(b.addedOn) - new Date(a.addedOn));
      });
    }
  }, [parsingCandidate?.candidates]);

  const handleRetryApi = React.useCallback(() => {
    // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'null'.
    intervalIdRef.current = setInterval(() => {
      dispatch(
        candidateResumeParsing(processedResumesPage, pageSize, showUnparsed),
      );
    }, 4000);
    return intervalIdRef.current;
  }, [newResumesPage, pageSize, showUnparsed, processedResumesPage]);

  const handleShowMoreNew = React.useCallback(async () => {
    setIsLoadingMore((prev) => ({...prev, new: true}));
    const nextPage = newResumesPage + 1;
    setNewResumesPage(nextPage);
    await dispatch(candidateResumeParsing(nextPage, pageSize, showUnparsed));
    // @ts-expect-error - TS2769 - No overload matches this call.
    clearInterval(intervalIdRef.current);
    handleRetryApi();
    setIsLoadingMore((prev) => ({...prev, new: false}));
  }, [newResumesPage, pageSize, showUnparsed]);

  const handleShowMoreProcessed = React.useCallback(async () => {
    setIsLoadingMore((prev) => ({...prev, processed: true}));
    const nextPage = processedResumesPage + 1;
    setProcessedResumesPage(nextPage);
    await dispatch(candidateResumeParsing(nextPage, pageSize, showUnparsed));
    setIsLoadingMore((prev) => ({...prev, processed: false}));
  }, [processedResumesPage, pageSize, showUnparsed]);

  React.useEffect(() => {
    const loadInitialData = async () => {
      setIsInitialLoading(true);
      try {
        await Promise.all([
          dispatch(
            candidateResumeParsing(
              processedResumesPage,
              pageSize,
              showUnparsed,
            ),
          ),
        ]);
        handleRetryApi();
      } finally {
        setIsInitialLoading(false);
      }
    };

    if (showParsingPanel) {
      loadInitialData();
    }

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        setProcessedResumesPage(0);
        setPageSize(10);
      }
    };
  }, [showParsingPanel, pageSize, showUnparsed]);

  if (isInitialLoading) {
    return (
      <PanelBody className={css.panelBody}>
        <div className={css.loaderContainer}>
          <CircularLoader size="large" />
        </div>
      </PanelBody>
    );
  }

  return (
    <PanelBody className={css.panelBody}>
      <div className={css.createOfferWrapper}>
        <div className={css.column}>
          <SubTitleSmall>{t('New_resumes', 'New resumes')}</SubTitleSmall>
          {allNewResumes.length > 0 ? (
            <>
              {allNewResumes.map((candidate) => (
                <InfoCard
                  // @ts-expect-error - TS2339 - Property 'addedOn' does not exist on type 'never'.
                  key={candidate.addedOn}
                  parsingCandidate={candidate}
                />
              ))}
            </>
          ) : (
            <Card classNames={{wrapper: css.emptyContainer}}>
              <SubTitleSmall color="tertiary">
                {t(
                  'No_candidate_resume_in_parsing',
                  'No candidate resume in parsing',
                )}
              </SubTitleSmall>
            </Card>
          )}
        </div>

        <div className={css.column}>
          <div className={css.rowSpaceBetween}>
            <SubTitleSmall>
              {t('Processed_resumes', 'Processed resumes')}
            </SubTitleSmall>
            <Checkbox
              name="unparsed-checkbox"
              onChange={() => {
                setShowUnparsed(!showUnparsed);
                setAllProcessedResumes([]); // Reset when filter changes
                setProcessedResumesPage(0);
              }}
              value="unparsed"
              checked={showUnparsed}
            >
              {t('Show_only_unparsed', 'Show only unparsed')}
            </Checkbox>
          </div>
          {allProcessedResumes.length > 0 ? (
            <>
              {allProcessedResumes.map((candidate) => (
                <InfoCard
                  // @ts-expect-error - TS2339 - Property 'addedOn' does not exist on type 'never'.
                  key={candidate.addedOn}
                  parsingCandidate={candidate}
                />
              ))}
              {parsingCandidate?.candidates.length > 0 ? (
                <div className={css.showMore}>
                  <Link
                    // @ts-expect-error - TS2769 - No overload matches this call.
                    variant="secondary"
                    as="buttonTextExtraSmall"
                    onClick={handleShowMoreProcessed}
                    className={css.showMoreButton}
                    disabled={isLoadingMore.processed}
                    underline={false}
                  >
                    {isLoadingMore.processed ? (
                      <CircularLoader size="small" />
                    ) : (
                      t('Show_More', 'Show More')
                    )}
                  </Link>
                </div>
              ) : null}
            </>
          ) : (
            <Card classNames={{wrapper: css.emptyContainer}}>
              <SubTitleSmall color="tertiary">
                {t('No_resumes_processed_yet', 'No resumes processed yet')}
              </SubTitleSmall>
            </Card>
          )}
        </div>
      </div>
    </PanelBody>
  );
};
